//@ts-nocheck

'use client'

import React, { useState, useEffect } from "react";

import * as S from "./UploadForm.styles";

import { Paragraph, Heading } from "../../atoms/Typography/Typography.component";
import Button from "../../atoms/Button/Button.component";
import { SpacerBase } from '../../atoms/GridSystem'
import Icon from "../../atoms/Icon/Icon.component";
import Spinner from "../../atoms/Spinner/Spinner.component";
import { useModal } from "../../../contexts/Modal.context";
import ModalAuth from "../Modal/ModalAuth/ModalAuth.component";
import { isFileTooLarge, classifyUploadResult, UploadResultLike } from "./UploadForm.utils";
import { MAX_UPLOAD_FILE_SIZE_BYTES } from "./UploadForm.constants";

import Image from 'next/image'
import { useTranslations } from "next-intl";

const UploadForm = ({ fileUpload, contestId, accessToken }: { fileUpload: (formData: FormData, contestId: number, accessToken: string) => Promise<UploadResultLike>, contestId: number, accessToken: string }) => {
  const [status, setStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle')
  const [file, setFile] = useState<File | null>(null)
  const [fileTitle, setFileTitle] = useState<string | null>('')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const { showModal } = useModal()
  const t = useTranslations();

  /**
   * Gestisce il cambiamento del file selezionato
   * @param {React.ChangeEvent<HTMLInputElement>} e - Evento change dell'input file
   * @returns {void}
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (isFileTooLarge(file, MAX_UPLOAD_FILE_SIZE_BYTES)) {
      e.target.value = ''
      showModal(ModalAuth, {
        title: t('privateArea.uploadFormFileTooLargeTitle'),
        description: t('privateArea.uploadFormFileTooLargeDescription'),
        foreground: true
      })
      return
    }

    setFile(file)

    // Pulisci l'URL precedente per evitare memory leak
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }

    // Crea un URL temporaneo per la preview dell'immagine
    const objectUrl = URL.createObjectURL(file)
    setPreviewUrl(objectUrl)
  }

  const handleFileUpload = (file: File) => {
    setStatus('uploading')
    const formData = new FormData()
    formData.append('photo', file)
    formData.append('title', fileTitle)

    fileUpload(formData, contestId, accessToken)
      .then((res) => {
        const outcome = classifyUploadResult(res)

        if (outcome === 'success') {
          handleSuccess()
          return
        }

        handleFailure()
      })
      .catch((err) => {
        // The Server Action can reject before its own error handling runs, e.g. when
        // Next.js refuses the request body for exceeding serverActions.bodySizeLimit.
        console.error(err)
        handleFailure()
      })
  }

  const handleFileTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileTitle(e.target.value)
  }

  const handleFailure = () => {
    setStatus('idle')

    showModal(ModalAuth, {
      title: t('privateArea.uploadFormModalErrorTitle'),
      description: t('privateArea.uploadFormModalErrorDescription'),
      foreground: true
    })
  }

  const handleSuccess = () => {
    setStatus('idle')
    setFile(null)
    setPreviewUrl(null)
    setFileTitle(null)

    showModal(ModalAuth, {
      title: t('privateArea.uploadFormModalSuccessTitle'),
      description: t('privateArea.uploadFormModalSuccessDescription'),
      foreground: true
    })
  }

  // Pulisci l'URL quando il componente viene smontato
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const title = previewUrl ? t('privateArea.uploadFormTitleUploaded') : t('privateArea.uploadFormTitle')

  return (
    <S.UploadFormWrapper className="upload-form-wrapper">
      <Heading 
        content={title}
        as={'h2'}
        typo={'headingXXL'}
        weight={'medium'}
      />
      <SpacerBase xs={2} xl={3} />
      <S.UploadFormCenterContent className="upload-form-center-content">
        { !previewUrl ? (
          <S.UploadFormFakeInput className="upload-form-fake-input" htmlFor="upload">
            <S.UploadFormIconWrapper className="upload-form-icon-wrapper">
              <Icon name={'upload'} useIconColor />
            </S.UploadFormIconWrapper>
            <SpacerBase xs={2} />
            <Paragraph 
              content={t('privateArea.uploadFormInputLabel')}
              typo={'headingMD'}
              weight={'medium'}
            />
            <S.UploadFormInputFile
              className="upload-form-input-file"
              type="file"
              onChange={handleFileChange}
              accept="image/*"
              id={'upload'}
            />
          </S.UploadFormFakeInput>
        ) : (
          <S.UploadFormImagePreviewWrapper className="image-preview-wrapper">
            <Image src={previewUrl} alt={t('privateArea.uploadFormImageAlt')} fill />
          </S.UploadFormImagePreviewWrapper>
        )}
      </S.UploadFormCenterContent>
      <SpacerBase xs={2} xl={3} />
      <S.UploadFormInputText
        className="upload-form-input-text"
        type="text"
        value={fileTitle || ''}
        onChange={handleFileTitleChange}
        placeholder={t('privateArea.uploadFormInputPlaceholder')}
      />
      <SpacerBase xs={2} xl={3} />
      <Button
        label={t('privateArea.uploadFormCta')}
        variant={'secondary'}
        iconEnd={'arrow-right'}
        inheritedColorScheme={'green'}
        handleClick={() => handleFileUpload(file)}
      />
      {status === 'uploading' && <Spinner />}
    </S.UploadFormWrapper>
  )
}

export default UploadForm